import { calcTotals, calcVATBreakdown } from '../utils/totalsCalculator';

/**
 * Generate a Code 128 barcode as an inline SVG string.
 * Code 128B encodes all printable ASCII characters.
 * This is a pure JS implementation — no library needed.
 */
function generateBarcodeSVG(text, width = 250, height = 50) {
  // Code 128B character set (values 0-95 map to ASCII 32-127)
  const CODE128B_START = 104;
  const CODE128_STOP = 106;

  // Bar widths for each Code 128 symbol (11 modules each, last bar = 2-module stop bar pattern)
  const CODE128_BARS = [
    '11011001100','11001101100','11001100110','10010011000','10010001100',
    '10001001100','10011001000','10011000100','10001100100','11001001000',
    '11001000100','11000100100','10110011100','10011011100','10011001110',
    '10111001100','10011101100','10011100110','11001110010','11001011100',
    '11001001110','11011100100','11001110100','11101101110','11101001100',
    '11100101100','11100100110','11101100100','11100110100','11100110010',
    '11011011000','11011000110','11000110110','10100011000','10001011000',
    '10001000110','10110001000','10001101000','10001100010','11010001000',
    '11000101000','11000100010','10110111000','10110001110','10001101110',
    '10111011000','10111000110','10001110110','11101110110','11010001110',
    '11000101110','11011101000','11011100010','11011101110','11101011000',
    '11101000110','11100010110','11101101000','11101100010','11100011010',
    '11101111010','11001000010','11110001010','10100110000','10100001100',
    '10010110000','10010000110','10000101100','10000100110','10110010000',
    '10110000100','10011010000','10011000010','10000110100','10000110010',
    '11000010010','11001010000','11110111010','11000010100','10001111010',
    '10100111100','10010111100','10010011110','10111100100','10011110100',
    '10011110010','11110100100','11110010100','11110010010','11011011110',
    '11011110110','11110110110','10101111000','10100011110','10001011110',
    '10111101000','10111100010','11110101000','11110100010','10111011110',
    '10111101110','11101011110','11110101110','11010000100','11010010000',
    '11010011100','1100011101011',
  ];

  const chars = Array.from(text);
  let checksum = CODE128B_START;
  const values = [CODE128B_START];
  chars.forEach((ch, i) => {
    const v = ch.charCodeAt(0) - 32;
    values.push(v);
    checksum += v * (i + 1);
  });
  values.push(checksum % 103);
  values.push(CODE128_STOP);

  const bars = values.map((v) => CODE128_BARS[v] || '').join('');
  const moduleWidth = width / (bars.length || 1);

  let x = 0;
  let rects = '';
  for (let i = 0; i < bars.length; i++) {
    const draw = bars[i] === '1';
    const start = i;
    let end = i;
    while (end < bars.length && bars[end] === bars[i]) end++;
    const w = (end - start) * moduleWidth;
    if (draw) {
      rects += `<rect x="${x.toFixed(2)}" y="0" width="${w.toFixed(2)}" height="${height}" fill="#000"/>`;
    }
    x += w;
    i = end - 1;
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">${rects}</svg>`;
}

export const getThermalReceiptTemplate = (
  cart,
  customer,
  storeInfo,
  selectedPaymentMethod,
  paidAmount,
  receiptNumber,
  discount = null,
  customVATRate = null,
  notes = '*** THANK YOU FOR YOUR VISIT ***', // ✅ Add notes parameter
  useMultiplePayments = false, // ✅ Multi-payment flag
  paymentMethodsArray = [], // ✅ Array of payment methods
  paymentMethods = [], // ✅ All available payment methods for lookup
  walkInCustomerName = '', // ✅ Walk-in customer name when no customer selected
  ticketType = 'Sale', // ✅ Ticket type: 'Sale' or 'Repair'
  pickupDate = null, // ✅ Pickup date for repair tickets
  isTvaIncluded = false, // ✅ Whether prices include VAT or not
  preCalculatedTotals = null // ✅ Optional pre-calculated totals from database to prevent rounding differences
) => {

  // ✅ Use pre-calculated totals from database if provided (for saved tickets)
  // ✅ Otherwise calculate from cart items using centralized utility (for new tickets during creation)
  const discountAmount = discount?.amount || 0;
  
  // Use centralized calculation utility for consistency
  const calculatedTotals = calcTotals(cart, discount, customVATRate, isTvaIncluded);
  
  // Override with pre-calculated totals if provided (for saved tickets from database)
  const subtotal = preCalculatedTotals?.subtotal ?? calculatedTotals.subtotal;
  const subtotalAfterDiscount = preCalculatedTotals?.subtotalAfterDiscount ?? subtotal;
  const totalVAT = preCalculatedTotals?.totalVAT ?? calculatedTotals.totalVAT;
  const totalIncl = preCalculatedTotals?.totalIncl ?? calculatedTotals.totalIncl;
  const subtotalExclVAT = subtotalAfterDiscount;

  // ✅ Calculate VAT breakdown using centralized utility
  const vatBreakdown = calcVATBreakdown(cart, discountAmount, customVATRate, isTvaIncluded);

  let vatSectionHtml = '';
  Object.keys(vatBreakdown).forEach((rate) => {
    const { base, vat } = vatBreakdown[rate];
    vatSectionHtml += `
      <tr>
        <td style="padding: 2px 0; font-weight: 900;">VAT ${rate}%:</td>
        <td style="text-align: right; font-weight: 900;">€${base.toFixed(2)}</td>
        <td style="text-align: right; font-weight: 900;">€${vat.toFixed(2)}</td>
      </tr>
    `;
  });

  const vatIndicator = customVATRate !== null && customVATRate !== undefined && customVATRate !== 21
    ? `<p style="font-size: 11px; color: #000; font-weight: 900;">⚠️ Custom VAT Rate Applied: ${customVATRate}%</p>`
    : '';

  const companyName = storeInfo?.name || storeInfo?.Name || storeInfo?.CompanyName || 'Your Company Name';
  const companyAddress = storeInfo?.address || storeInfo?.Address || (storeInfo?.Street && storeInfo?.StreetNumber ? `${storeInfo.Street} ${storeInfo.StreetNumber}` : '') || '123 Street Address';
  const companyCity = storeInfo?.city || storeInfo?.City || 'City';
  const companyPostCode = storeInfo?.postCode || storeInfo?.PostCode || '';
  const companyCityFull = companyPostCode ? `${companyPostCode} ${companyCity}` : companyCity;
  const companyCountry = storeInfo?.country || storeInfo?.Country || '';
  const companyPhone = storeInfo?.phone || storeInfo?.Phone || storeInfo?.PhoneNumber || '+32 123 456 789';
  const companyVAT = storeInfo?.vat || storeInfo?.VATNumber || storeInfo?.BTWNumber || storeInfo?.TaxNumber || 'BE 0123.456.789';
  const companyEmail = storeInfo?.email || storeInfo?.Email || '';
  const companyWebsite = storeInfo?.website || storeInfo?.Website || '';

  // ✅ Determine ticket title based on type
  const ticketTitle = ticketType === 'Quotation'
    ? 'DEVIS / QUOTATION'
    : ticketType === 'Repair' 
    ? 'REPARATION TICKET DE CAISSE' 
    : 'TICKET DE CAISSE';

  // ✅ Format pickup date if present
  const pickupDateFormatted = pickupDate 
    ? new Date(pickupDate).toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      })
    : null;

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <style>
        @page {
          size: 72mm auto;
          margin: 0;
        }
        html {
          margin: 0;
          padding: 0;
        }
        body {
          margin: 0;
          padding: 0;
          width: 72mm;
        }
        @media print {
          @page {
            size: 72mm auto;
            margin: 0;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            color-adjust: exact;
            width: 72mm !important;
          }
        }
        * { 
          margin: 0; 
          padding: 0; 
          box-sizing: border-box;
        }
        body {
          font-family: 'Courier New', Courier, monospace;
          font-size: 12px;
          line-height: 1.4;
          color: #000;
          background: white;
          padding: 10px;
          width: 72mm;
          font-weight: 700;
          -webkit-font-smoothing: none;
          -moz-osx-font-smoothing: grayscale;
        }
        .receipt-container { 
          width: 100%;
        }
        .header { 
          text-align: center; 
          margin-bottom: 15px; 
          border-bottom: 2px dashed #000; 
          padding-bottom: 10px; 
        }
        .header h1 { 
          font-size: 20px; 
          font-weight: 900; 
          margin-bottom: 5px;
          letter-spacing: 1px;
        }
        .header p { 
          font-size: 11px; 
          margin: 2px 0; 
          line-height: 1.3;
          font-weight: 700;
        }
        .header strong {
          font-weight: 900;
        }
        .info-section { 
          margin: 10px 0; 
          font-size: 12px;
          font-weight: 700;
        }
        .info-row { 
          display: flex; 
          justify-content: space-between; 
          margin: 3px 0;
          font-weight: 700;
        }
        .info-row strong {
          font-weight: 900;
        }
        table { 
          width: 100%; 
          border-collapse: collapse; 
          margin: 10px 0;
          table-layout: fixed;
        }
        th { 
          text-align: left; 
          border-bottom: 2px solid #000; 
          padding: 5px 2px; 
          font-size: 11px;
          font-weight: 900;
        }
        th:first-child { width: 45%; }
        th:nth-child(2) { width: 15%; }
        th:nth-child(3) { width: 20%; }
        th:nth-child(4) { width: 20%; }
        td { 
          padding: 5px 2px; 
          font-size: 11px;
          font-weight: 700;
          word-wrap: break-word;
          word-break: break-word;
          overflow-wrap: break-word;
        }
        .item-row td { 
          border-bottom: 1px dotted #000; 
        }
        .total-section { 
          margin-top: 15px; 
          border-top: 2px solid #000; 
          padding-top: 10px; 
        }
        .total-row { 
          display: flex; 
          justify-content: space-between; 
          margin: 5px 0; 
          font-weight: 800;
          font-size: 12px;
        }
        .discount-row {
          background: #000;
          color: #fff;
          padding: 5px;
          margin: 5px 0;
          border: 2px solid #000;
          font-weight: 900;
        }
        .discount-label {
          font-size: 11px;
          font-weight: 900;
        }
        .after-discount {
          background: #000;
          color: #fff;
          padding: 5px;
          margin: 5px 0;
          font-weight: 900;
          font-size: 12px;
        }
        .grand-total { 
          font-size: 17px;
          font-weight: 900;
          margin-top: 10px;
          padding-top: 5px;
          border-top: 3px double #000;
        }
        .savings-banner {
          text-align: center;
          background: #000;
          color: #fff;
          padding: 8px;
          margin: 10px 0;
          font-weight: 900;
          font-size: 12px;
        }
        .footer { 
          text-align: left; 
          margin-top: 20px; 
          border-top: 2px dashed #000; 
          padding-top: 10px; 
          font-size: 11px;
          font-weight: 700;
        }
        .footer p {
          font-weight: 700;
        }
        .footer strong {
          font-weight: 900;
        }
        .payment-info { 
          margin: 10px 0; 
          padding: 10px; 
          background: #000; 
          color: #fff;
          border: 2px solid #000;
          font-weight: 800;
        }
        .payment-info .total-row {
          color: #fff;
          font-size: 12px;
        }
        .vat-indicator {
          background: #000;
          color: #fff;
          padding: 5px;
          margin: 5px 0;
          border: 2px solid #000;
          text-align: center;
          font-size: 10px;
          font-weight: 900;
        }
        strong, b {
          font-weight: 900 !important;
        }
      </style>
    </head>
    <body>
      <div class="receipt-container">
        <div class="header">
          <h1 style="text-align: center;">${ticketTitle}</h1>
          <p><strong>${companyName}</strong></p>
          <p>${companyAddress}</p>
          <p>${companyCityFull}${companyCountry ? ', ' + companyCountry : ''}</p>
          <p>Tel: ${companyPhone}</p>
          <p>TVA: ${companyVAT}</p>
          ${vatIndicator ? `<div class="vat-indicator">${vatIndicator}</div>` : ''}
          ${ticketType === 'Repair' && pickupDateFormatted ? `
            <div style="margin-top: 10px; padding: 8px; background: #000; color: #fff; text-align: center; font-weight: 900;">
              <p style="margin: 0; font-size: 13px;">📅 PICKUP DATE</p>
              <p style="margin: 2px 0 0 0; font-size: 15px; font-weight: 900;">${pickupDateFormatted}</p>
            </div>
          ` : ''}
        </div>

        <div class="info-section">
          <div class="info-row">
            <span>Receipt #:</span>
            <span><strong>${receiptNumber}</strong></span>
          </div>
          <div class="info-row">
            <span>Date:</span>
            <span><strong>${new Date().toLocaleDateString('fr-BE')} ${new Date().toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' })}</strong></span>
          </div>
          ${customer || walkInCustomerName ? `
            <div class="info-row">
              <span>Client:</span>
              <span><strong>${customer ? (customer.Name || 'N/A') : walkInCustomerName}</strong></span>
            </div>
            ${customer && customer.PhoneNumber && ticketType === 'Repair' ? `
              <div class="info-row">
                <span>Phone:</span>
                <span><strong>${customer.PhoneNumber}</strong></span>
              </div>
            ` : ''}
          ` : ''}
          <div class="info-row">
            <span>Cashier:</span>
            <span><strong>Admin</strong></span>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 45%;">Product</th>
              <th style="width: 15%; text-align: center;">Qty</th>
              <th style="width: 20%; text-align: right;">Price</th>
              <th style="width: 20%; text-align: right;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${cart.map(item => `
              <tr class="item-row">
                <td style="width: 45%; word-wrap: break-word;">
                  <strong>${item.Name || 'N/A'}</strong>
                  ${item.Description ? `<br><span style="font-size: 9px; color: #666;">${item.Description}</span>` : ''}
                  ${item.SerialNumber ? `<br><span style="font-size: 9px; font-weight: 900;">SN: ${item.SerialNumber}</span>` : ''}
                </td>
                <td style="width: 15%; text-align: center;"><strong>${item.Quantity}</strong></td>
                <td style="width: 20%; text-align: right;"><strong>€${item.Price.toFixed(2)}</strong></td>
                <td style="width: 20%; text-align: right;"><strong>€${(item.Quantity * item.Price).toFixed(2)}</strong></td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="total-section">
          <div class="total-row">
            <span><strong>Subtotal${isTvaIncluded ? '' : ' (Excl. VAT)'}:</strong></span>
            <span><strong>€${subtotal.toFixed(2)}</strong></span>
          </div>
          
          ${discount ? `
          <div class="discount-row">
            <div style="display: flex; justify-content: space-between;">
              <div>
                <span class="discount-label">DISCOUNT ${discount.type === 'percentage' ? `(${discount.value}%)` : ''}</span>
                ${discount.reason ? `<br><span style="font-size: 10px; font-weight: 900;">${discount.reason}</span>` : ''}
              </div>
              <span class="discount-label">-€${discountAmount.toFixed(2)}</span>
            </div>
          </div>
          <div class="after-discount">
            <div style="display: flex; justify-content: space-between;">
              <span><strong>After Discount:</strong></span>
              <span><strong>€${totalIncl.toFixed(2)}</strong></span>
            </div>
          </div>
          ` : ''}
          
          <div class="total-row">
            <span><strong>Total VAT:</strong></span>
            <span><strong>€${totalVAT.toFixed(2)}</strong></span>
          </div>
          
          <div class="total-row grand-total">
            <span><strong>TOTAL (Incl. VAT):</strong></span>
            <span><strong>€${totalIncl.toFixed(2)}</strong></span>
          </div>
        </div>

        ${discount ? `
        <div class="savings-banner">
          ★ YOU SAVED €${discountAmount.toFixed(2)} ★
        </div>
        ` : ''}

        <div class="payment-info">
          ${useMultiplePayments && paymentMethodsArray.length > 0 ? `
            <div class="total-row">
              <span><strong>Payment Methods:</strong></span>
            </div>
            ${paymentMethodsArray.map(pm => {
              const method = paymentMethods.find(m => m.id === pm.method || m.value === pm.method);
              const methodName = method ? (method.name || method.label) : pm.method;
              return `
                <div class="total-row" style="font-size: 11px; margin-left: 10px;">
                  <span>${methodName}:</span>
                  <span>€${parseFloat(pm.amount || 0).toFixed(2)}</span>
                </div>
              `;
            }).join('')}
            <div class="total-row">
              <span><strong>Total Paid:</strong></span>
              <span><strong>€${ticketType === 'Sale' ? totalIncl.toFixed(2) : paymentMethodsArray.reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0).toFixed(2)}</strong></span>
            </div>
          ` : (() => {
              // ✅ For Sale tickets, paid amount is ALWAYS the total (full payment required)
              // ✅ For Repair tickets, paid amount can be partial
              const paid = ticketType === 'Sale' ? totalIncl : (parseFloat(paidAmount) || 0);
              const remaining = totalIncl - paid;
              
              // Get payment method name (handles both object and string)
              const paymentMethodName = selectedPaymentMethod?.Name || selectedPaymentMethod?.name || selectedPaymentMethod || '';
              
              // ✅ Only show payment method when there's actual payment, but always show paid amount
              return `
                ${paid > 0 ? `
                  <div class="total-row">
                    <span><strong>Payment Method:</strong></span>
                    <span><strong>${paymentMethodName || 'N/A'}</strong></span>
                  </div>
                ` : ''}
                <div class="total-row">
                  <span><strong>Paid:</strong></span>
                  <span><strong>€${paid.toFixed(2)}</strong></span>
                </div>
                ${ticketType === 'Repair' && remaining > 0.01 ? `
                  <div class="total-row" style="color: #fff; font-weight: 900;">
                    <span><strong>Payable Amount:</strong></span>
                    <span><strong>€${remaining.toFixed(2)}</strong></span>
                  </div>
                ` : ''}
              `;
            })()}
        </div>

        <div class="footer">
          <p><strong>${notes || '*** THANK YOU FOR YOUR VISIT ***'}</strong></p>
          ${discount ? `<p style="margin-top: 5px; font-weight: 900;">Discount saved you €${discountAmount.toFixed(2)}</p>` : ''}
        </div>

        ${receiptNumber ? `
        <div style="text-align: center; margin-top: 14px; padding-top: 10px; border-top: 1px dashed #000;">
          ${generateBarcodeSVG(receiptNumber, 240, 48)}
          <div style="font-size: 10px; font-family: 'Courier New', monospace; font-weight: 900; letter-spacing: 2px; margin-top: 4px;">${receiptNumber}</div>
        </div>
        ` : ''}
      </div>
    </body>
    </html>
  `;
};